import type { ChatTurnResponse } from '../types/krybot';

/** Tool budget large enough to orient, load a skill, write, and finish. */
export const CHAT_JOURNAL_MAX_STEPS = 24;

export const CONTROL_BRIDGE_MODEL_CHANGED_EVENT = 'krysalis:control-bridge-model-changed';

export type ChatWorkspaceToolInvocation = {
  auto: true;
  journal_tools: true;
  write_verbs: boolean;
  max_steps: number;
};

export type ChatWorkspaceToolInvocationOptions = {
  /** When set, tools are omitted unless ``workspaceSlug`` equals this authorized company slug. */
  authorizedWorkspaceSlug?: string | null;
  /** Default true. Platform/chat read-only surfaces pass false. */
  allowWriteVerbs?: boolean;
};

/**
 * Build Krybot ``tool_invocation`` for Control Bridge chat.
 * Permission scoping: refuse tools when the requested slug is not the authorized company slug.
 */
export function chatWorkspaceToolInvocation(
  workspaceSlug?: string | null,
  options?: ChatWorkspaceToolInvocationOptions
): ChatWorkspaceToolInvocation | undefined {
  const slug = typeof workspaceSlug === 'string' ? workspaceSlug.trim() : '';
  if (!slug) return undefined;
  const authorized =
    typeof options?.authorizedWorkspaceSlug === 'string'
      ? options.authorizedWorkspaceSlug.trim()
      : '';
  if (authorized && authorized !== slug) {
    return undefined;
  }
  return {
    auto: true,
    journal_tools: true,
    write_verbs: options?.allowWriteVerbs !== false,
    max_steps: CHAT_JOURNAL_MAX_STEPS,
  };
}

function receiptList(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return [];
  return value.filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === 'object');
}

export function chatTurnMutatedModel(response: ChatTurnResponse | null | undefined): boolean {
  const run = response?.agent_run;
  const output = run?.output_payload;
  const snapshot = run?.model_snapshot;
  const assistant = response?.assistant_message as
    | { metadata?: Record<string, unknown>; llm_metadata?: Record<string, unknown> }
    | undefined;
  const receipts = [
    ...receiptList(output && typeof output === 'object' ? output.tool_receipts : undefined),
    ...receiptList(snapshot && typeof snapshot === 'object' ? snapshot.tool_receipts : undefined),
    ...receiptList(
      output && typeof output === 'object' && output.tool_invocation_result
        ? (output.tool_invocation_result as { receipts?: unknown }).receipts
        : undefined
    ),
    ...receiptList(assistant?.metadata?.tool_receipts),
    ...receiptList(assistant?.llm_metadata?.tool_receipts),
    ...receiptList(
      assistant?.llm_metadata?.model_snapshot &&
        typeof assistant.llm_metadata.model_snapshot === 'object'
        ? (assistant.llm_metadata.model_snapshot as { tool_receipts?: unknown }).tool_receipts
        : undefined
    ),
  ];
  return receipts.some((row) => {
    const tool = String(row.tool || '').trim();
    if (tool !== 'kryspace_write') return false;
    if (row.success === false) return false;
    const result = row.result;
    if (result && typeof result === 'object' && (result as { ok?: unknown }).ok === false) {
      return false;
    }
    return true;
  });
}

export function notifyControlBridgeModelChanged(workspaceSlug?: string | null): void {
  if (typeof window === 'undefined') return;
  const slug = typeof workspaceSlug === 'string' ? workspaceSlug.trim() : '';
  window.dispatchEvent(
    new CustomEvent(CONTROL_BRIDGE_MODEL_CHANGED_EVENT, {
      detail: slug ? { slug } : {},
    })
  );
}

export function subscribeControlBridgeModelChanged(
  workspaceSlug: string | null | undefined,
  onChanged: () => void
): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  const expected = (workspaceSlug || '').trim();
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ slug?: string }>).detail;
    const incoming = typeof detail?.slug === 'string' ? detail.slug.trim() : '';
    if (expected && incoming && incoming !== expected) return;
    onChanged();
  };
  window.addEventListener(CONTROL_BRIDGE_MODEL_CHANGED_EVENT, handler);
  return () => window.removeEventListener(CONTROL_BRIDGE_MODEL_CHANGED_EVENT, handler);
}
